import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import Logo from '../common/Logo';
import { Menu, X, Sun, Moon, Globe, ChevronDown } from 'lucide-react';

const SiteNav = () => {
    const { user } = useAuth();
    const { theme, toggleMode } = useTheme();
    const { i18n, t } = useTranslation();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [langMenuOpen, setLangMenuOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        { name: t('landing.features', 'Özellikler'), path: '/#features' },
        { name: t('landing.whyUgra', 'Neden ugra?'), path: '/why-ugra' },
        { name: t('landing.pricing', 'Fiyatlandırma'), path: '/pricing' },
        { name: t('landing.references', 'Referanslarımız'), path: '/referanslarimiz' },
        { name: t('landing.about', 'Hakkımızda'), path: '/about' },
        { name: t('landing.contact', 'İletişim'), path: '/contact' },
    ];

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
        setLangMenuOpen(false);
        setMobileMenuOpen(false);
    };

    const isDark = theme.mode === 'dark';

    const isHome = location.pathname === '/';

    return (
        <nav style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: scrolled ? '1rem 10%' : '1.5rem 10%',
            background: scrolled ? (isDark ? 'rgba(10, 7, 5, 0.95)' : 'rgba(255, 255, 255, 0.95)') : 'transparent',
            backdropFilter: scrolled ? 'blur(20px)' : 'none',
            position: 'fixed',
            top: 0,
            width: '100%',
            zIndex: 1000,
            boxSizing: 'border-box',
            transition: 'all 0.3s ease',
            borderBottom: scrolled ? (isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)') : 'none'
        }}>
            <Link to="/" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontWeight: 600,
                fontSize: '2rem',
                textDecoration: 'none',
                color: 'var(--primary)',
                fontFamily: 'var(--font-logo)'
            }}>
                <Logo size={40} /> <span style={{position: 'relative', top: '2px'}}>ugra</span>
            </Link>

            {/* Desktop Nav */}
            <div className="desktop-links" style={{
                display: 'none',
                gap: '1.5rem',
                alignItems: 'center',
                padding: '8px 12px 8px 24px',
                background: isDark ? 'rgba(26, 20, 18, 0.4)' : 'rgba(255, 255, 255, 0.3)',
                backdropFilter: 'blur(20px)',
                borderRadius: '24px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: scrolled ? '0 10px 30px -10px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.3s ease'
            }}>
                <div style={{ display: 'flex', gap: '1.5rem' }}>
                    {navLinks.map(link => (
                        <Link
                            key={link.path}
                            to={link.path}
                            style={{
                                color: 'var(--text-main)',
                                textDecoration: 'none',
                                fontWeight: 600,
                                fontSize: '0.8125rem',
                                transition: 'all 0.2s',
                                padding: '8px 4px',
                                opacity: 0.8
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = 'var(--primary)';
                                e.target.style.opacity = '1';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = 'var(--text-main)';
                                e.target.style.opacity = '0.8';
                            }}
                        >
                            {link.name}
                        </Link>
                    ))}
                </div>

                <div style={{ width: '1px', height: '20px', background: 'var(--border)', margin: '0 8px', opacity: 0.3 }}></div>

                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    {/* Theme Toggle */}
                    <button
                        onClick={toggleMode}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--text-main)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '8px',
                            borderRadius: '10px',
                            transition: 'all 0.2s',
                            opacity: 0.7
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(111, 78, 55, 0.1)'; e.currentTarget.style.opacity = 1; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.opacity = 0.7; }}
                    >
                        {isDark ? <Sun size={20} /> : <Moon size={20} />}
                    </button>

                    {/* Language Selector */}
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setLangMenuOpen(!langMenuOpen);
                            }}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'var(--text-main)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '8px',
                                borderRadius: '10px',
                                transition: 'all 0.2s',
                                opacity: 0.8,
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                minWidth: '80px',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(111, 78, 55, 0.1)'; e.currentTarget.style.opacity = 1; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.opacity = 0.8; }}
                        >
                            <Globe size={18} /> {(i18n.language || 'TR').toUpperCase()} <ChevronDown size={14} style={{ transform: langMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                        </button>

                        {langMenuOpen && (
                            <div style={{
                                position: 'absolute',
                                top: '120%',
                                right: 0,
                                background: 'var(--bg-card)',
                                border: '1px solid var(--border)',
                                borderRadius: '12px',
                                padding: '8px',
                                minWidth: '140px',
                                boxShadow: 'var(--shadow-md)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                                zIndex: 1001,
                                animation: 'fadeIn 0.2s ease-out'
                            }}>
                                <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
                                <button
                                    onClick={() => changeLanguage('tr')}
                                    style={{
                                        background: i18n.language === 'tr' ? 'rgba(111, 78, 55, 0.1)' : 'transparent',
                                        border: 'none',
                                        color: i18n.language === 'tr' ? 'var(--primary)' : 'var(--text-main)',
                                        padding: '10px 12px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        fontWeight: 600,
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    🇹🇷 Türkçe
                                </button>
                                <button
                                    onClick={() => changeLanguage('en')}
                                    style={{
                                        background: i18n.language === 'en' ? 'rgba(111, 78, 55, 0.1)' : 'transparent',
                                        border: 'none',
                                        color: i18n.language === 'en' ? 'var(--primary)' : 'var(--text-main)',
                                        padding: '10px 12px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        fontWeight: 600,
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    🇺🇸 English
                                </button>
                            </div>
                        )}
                    </div>

                    {user ? (
                        <Link to="/app" className="ai-glow-button" style={{
                            padding: '0.65rem 1.5rem',
                            borderRadius: '14px',
                            textDecoration: 'none',
                            background: 'var(--primary)',
                            color: 'white',
                            fontSize: '0.8125rem',
                            fontWeight: 800
                        }}>
                            {t('nav.overview', 'Panel')}
                        </Link>
                    ) : (
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <Link to="/login" className="login-glow-button" style={{
                                padding: '0.65rem 1.25rem',
                                borderRadius: '14px',
                                textDecoration: 'none',
                                background: isDark ? 'rgba(255,255,255,0.05)' : 'white',
                                color: 'var(--text-main)',
                                fontWeight: '700',
                                fontSize: '0.8125rem',
                                border: '1px solid var(--border)'
                            }}>{t('login.signIn', 'Giriş')}</Link>
                            <Link to="/signup" className="ai-glow-button" style={{
                                padding: '0.65rem 1.5rem',
                                borderRadius: '14px',
                                textDecoration: 'none',
                                background: 'var(--primary)',
                                color: 'white',
                                fontWeight: '800',
                                fontSize: '0.8125rem'
                            }}>
                                {t('landing.getStarted', 'Ücretsiz Başla')}
                            </Link>
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                    onClick={toggleMode}
                    className="mobile-only"
                    style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--text-main)',
                        padding: '8px',
                        display: 'none'
                    }}
                >
                    {isDark ? <Sun size={24} /> : <Moon size={24} />}
                </button>
                <button
                    className="mobile-toggle"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    style={{ background: 'transparent', color: 'var(--text-main)', border: 'none', padding: '8px', display: 'none' }}
                >
                    {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                </button>
            </div>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '100vh',
                    background: 'var(--bg-card)',
                    zIndex: 999,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '6rem 10% 2rem',
                    gap: '1.5rem',
                    animation: 'slideIn 0.3s ease-out'
                }}>
                    <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

                    <button
                        onClick={() => setMobileMenuOpen(false)}
                        style={{
                            position: 'absolute',
                            top: '1.5rem',
                            right: '10%',
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--text-main)',
                            padding: '8px',
                            cursor: 'pointer'
                        }}
                    >
                        <X size={32} />
                    </button>

                    {navLinks.map(link => (
                        <Link
                            key={link.path}
                            to={link.path}
                            onClick={() => setMobileMenuOpen(false)}
                            style={{ fontSize: '1.5rem', fontWeight: 800, textDecoration: 'none', color: 'var(--text-main)', letterSpacing: '-1px' }}
                        >
                            {link.name}
                        </Link>
                    ))}

                    <div style={{ width: '100%', height: '1px', background: 'var(--border)', margin: '1rem 0' }}></div>

                    {/* Mobile Language Selector */}
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <button
                            onClick={() => changeLanguage('tr')}
                            style={{
                                flex: 1,
                                padding: '1rem',
                                borderRadius: '12px',
                                background: i18n.language === 'tr' ? 'var(--primary)' : 'var(--bg-main)',
                                color: i18n.language === 'tr' ? 'white' : 'var(--text-main)',
                                border: '1px solid var(--border)',
                                fontWeight: 700,
                                fontSize: '1rem'
                            }}
                        >
                            🇹🇷 Türkçe
                        </button>
                        <button
                            onClick={() => changeLanguage('en')}
                            style={{
                                flex: 1,
                                padding: '1rem',
                                borderRadius: '12px',
                                background: i18n.language === 'en' ? 'var(--primary)' : 'var(--bg-main)',
                                color: i18n.language === 'en' ? 'white' : 'var(--text-main)',
                                border: '1px solid var(--border)',
                                fontWeight: 700,
                                fontSize: '1rem'
                            }}
                        >
                            🇺🇸 English
                        </button>
                    </div>

                    <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {user ? (
                            <Link to="/app" className="ai-glow-button" style={{ padding: '1.25rem', textAlign: 'center', borderRadius: '16px', textDecoration: 'none', background: 'var(--primary)', color: 'white', fontWeight: 800 }}>
                                {t('nav.overview', 'Panelime Git')}
                            </Link>
                        ) : (
                            <>
                                <Link to="/login" onClick={() => setMobileMenuOpen(false)} style={{ padding: '1.25rem', textAlign: 'center', borderRadius: '16px', textDecoration: 'none', color: 'var(--text-main)', border: '1px solid var(--border)', fontWeight: 700 }}>
                                    {t('login.signIn', 'Giriş Yap')}
                                </Link>
                                <Link to="/signup" onClick={() => setMobileMenuOpen(false)} className="ai-glow-button" style={{ padding: '1.25rem', textAlign: 'center', borderRadius: '16px', textDecoration: 'none', background: 'var(--primary)', color: 'white', fontWeight: 800 }}>
                                    {t('landing.getStarted', 'Ücretsiz Başla')}
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            )}

            <style>{`
                @media (min-width: 1024px) {
                    .desktop-links { display: flex !important; }
                }
                @media (max-width: 1023px) {
                    .mobile-toggle { display: block !important; }
                    .mobile-only { display: block !important; }
                }

                .ai-glow-button, .login-glow-button {
                    position: relative;
                    overflow: hidden;
                    transition: all 0.3s ease;
                }

                .ai-glow-button::after, .login-glow-button::after {
                    content: '';
                    position: absolute;
                    top: -50%;
                    left: -50%;
                    width: 200%;
                    height: 200%;
                    background: linear-gradient(
                        45deg,
                        transparent,
                        rgba(255, 255, 255, 0.4),
                        transparent
                    );
                    transform: rotate(45deg);
                    animation: shimmer 3s infinite;
                }

                @keyframes shimmer {
                    0% { transform: translateX(-150%) rotate(45deg); }
                    100% { transform: translateX(150%) rotate(45deg); }
                }

                .ai-glow-button:hover, .login-glow-button:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(var(--primary-rgb), 0.2);
                }

                .ai-glow-button:hover {
                    box-shadow: 0 8px 25px rgba(var(--primary-rgb), 0.4);
                }
            `}</style>
        </nav>
    );
};

export default SiteNav;
